import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { useDismiss } from '../shared/lib/use-dismiss';
import { onSessionExpired } from '../shared/api/client';
import { api, type Profile } from '../shared/api/endpoints';
import { LocaleProvider, useLocale, useMessages } from '../shared/i18n/locale';
import { initials } from '../shared/lib/format';
import { EmptyState, pillStyle } from '../shared/ui/primitives';
import { DirectoriesPage } from '../pages/DirectoriesPage';
import { FlowPage } from '../pages/FlowPage';
import { ImportPage } from '../pages/ImportPage';
import { LiveMapPage } from '../pages/LiveMapPage';
import { LoginPage } from '../pages/LoginPage';
import { PublicTrackingPage } from '../pages/PublicTrackingPage';
import { IntegrationsPage, ParametersPage, SloPage } from '../pages/OpsPage';
import { PickupMatrixPage } from '../pages/PickupMatrixPage';
import { AuditPage } from '../pages/AuditPage';
import { ResolutionsPage } from '../pages/ResolutionsPage';
import { RunsPage } from '../pages/RunsPage';
import { TemplatesPage } from '../pages/TemplatesPage';
import { ReturnsPage } from '../pages/ReturnsPage';
import { UsersPage } from '../pages/UsersPage';
import { DepartmentsContext } from '../shared/lib/departments';
import { Logo } from '../shared/ui/Logo';
import {
  PRIMARY_MENU,
  SETTINGS_MENU,
  SINGLE_SCREEN,
  entryFor,
  redirectFor,
  visibleEntries,
} from './menu';
import { useRoute } from './router';
import './tokens.css';

/**
 * Оболочка кабинета (разд. 3 спецификации).
 *
 * Меню собирается **из ответа сервера**, а не из роли на клиенте: собранное по своему
 * пониманию прав, оно рано или поздно покажет пункт, который отдаёт `403`. Элемент,
 * недоступный роли, не рендерится вовсе — спрятанная кнопка честнее неактивной.
 *
 * Направление показано в topbar и подчёркнуто полосой: инсталляции физически
 * разделены (FR-413), но администратор группы держит открытыми две вкладки, и
 * ошибиться направлением дороже, чем выглядеть строго.
 */
export function App(): ReactElement {
  return (
    <LocaleProvider>
      <Cabinet />
    </LocaleProvider>
  );
}

function Cabinet(): ReactElement {
  const messages = useMessages();
  const { locale, setLocale } = useLocale();
  const { route, navigate, setParams } = useRoute();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [checked, setChecked] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(readStoredTheme);

  /*
   * Страница отслеживания — публичная (FR-594): получатель открывает её по ссылке из
   * СМС и входа не имеет. Проверка профиля для неё не запускается вовсе, иначе
   * человек увидит форму входа вместо своего заказа.
   */
  const publicCode = route.section === 't' ? (route.params.get('code') ?? publicSegment()) : null;

  useEffect(() => {
    if (publicCode !== null) {
      setChecked(true);
      return;
    }

    void api
      .profile()
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setChecked(true));
  }, [publicCode]);

  // Сессию не обновить — на форму входа, а не пустой кабинет с ошибками на каждом экране.
  useEffect(() => {
    onSessionExpired(() => setProfile(null));

    return () => onSessionExpired(null);
  }, []);

  useEffect(() => {
    document.documentElement.dataset['theme'] = theme;

    try {
      window.localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Заблокированное хранилище — тема просто не переживёт перезагрузку.
    }
  }, [theme]);

  // Старый адрес раздела (`/drivers`, `/slo`, `/import`) — на новое место в меню.
  useEffect(() => {
    if (profile === null) {
      return;
    }

    const target = redirectFor(route.section, profile.sections);

    // Ручной ввод убран из интерфейса: заказы приходят только из интеграции. Импорт
    // переехал в настройки отдельным пунктом.
    if (page === 'create' || page === 'import') {
      const rest = Object.fromEntries(route.params);
      delete rest['page'];
      navigate(page === 'import' ? 'import' : route.section, page === 'import' ? {} : rest, {
        replace: true,
      });

      return;
    }

    if (target !== null) {
      navigate(
        target.section,
        { ...Object.fromEntries(route.params), ...target.params },
        {
          replace: true,
        },
      );
    }
  }, [profile, route.section, route.params.get('page')]);

  if (!checked) {
    return <main style={{ padding: 'var(--sp-8)' }}>{messages.cabinet.common.loading}</main>;
  }

  if (publicCode !== null) {
    return <PublicTrackingPage code={publicCode} />;
  }

  if (profile === null) {
    return (
      <LoginPage
        onSuccess={() => {
          void api.profile().then(setProfile);
        }}
      />
    );
  }

  const sections = profile.sections;
  const page = route.params.get('page');
  const entry = entryFor(route.section, sections);
  const requestedTab = route.params.get('tab') ?? '';
  // Раздел, который рисуется: вкладка пункта меню или раздел вне меню (карта).
  const section =
    entry !== null
      ? entry.tabs.includes(requestedTab)
        ? requestedTab
        : (entry.tabs[0] ?? 'flow')
      : sections.includes(route.section)
        ? route.section
        : (visibleEntries(PRIMARY_MENU, sections)[0]?.code ?? 'flow');
  const canEdit = profile.role === 'admin';

  return (
    <DepartmentsContext.Provider value={profile.modes}>
      <div style={{ minHeight: '100vh', display: 'grid', gridTemplateRows: 'auto 1fr' }}>
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-6)',
            padding: 'var(--sp-3) var(--gutter)',
            minHeight: 'var(--topbar-h)',
            background: 'var(--surface)',
            borderBottom: '1px solid var(--line)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Logo name={messages.cabinet.common.brand} />

          <TopNav
            sections={sections}
            active={entry?.code ?? route.section}
            onSelect={(code) => navigate(code)}
          />

          <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
            <InstallationSwitch
              current={profile.installation_code}
              peers={profile.peer_installations ?? []}
            />

            {/* Код текущего языка; нажатие переключает на другой, подсказка говорит на какой. */}
            <button
              type="button"
              style={{ ...pillStyle, padding: 0, width: 'var(--pill-h)', justifyContent: 'center' }}
              title={locale === 'ru' ? messages.languages.uzLatn : messages.languages.ru}
              aria-label={locale === 'ru' ? messages.languages.uzLatn : messages.languages.ru}
              onClick={() => setLocale(locale === 'ru' ? 'uz-Latn' : 'ru')}
            >
              {locale === 'ru' ? 'RU' : 'UZ'}
            </button>

            <button
              type="button"
              style={{ ...pillStyle, padding: 0, width: 'var(--pill-h)', justifyContent: 'center' }}
              aria-pressed={theme === 'dark'}
              aria-label={
                theme === 'light'
                  ? messages.cabinet.common.themeDark
                  : messages.cabinet.common.themeLight
              }
              title={
                theme === 'light'
                  ? messages.cabinet.common.themeDark
                  : messages.cabinet.common.themeLight
              }
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            >
              {theme === 'light' ? <MoonIcon /> : <SunIcon />}
            </button>

            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                marginLeft: 'var(--sp-2)',
                color: 'var(--text-2)',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 'var(--mark)',
                  height: 'var(--mark)',
                  borderRadius: 'var(--r-pill)',
                  background: 'var(--accent-soft)',
                  color: 'var(--accent-ink)',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 'var(--fs-xs)',
                  fontWeight: 'var(--fw-bold)',
                }}
              >
                {initials(profile.full_name)}
              </span>
              <span style={{ whiteSpace: 'nowrap' }}>{profile.full_name}</span>
              <button
                type="button"
                onClick={() => {
                  void api.logout().then(() => setProfile(null));
                }}
                style={{
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  color: 'var(--accent)',
                  fontSize: 'var(--fs)',
                }}
              >
                {messages.cabinet.common.logout}
              </button>
            </span>
          </div>
        </header>

        <main
          style={{
            width: '100%',
            maxWidth: 'var(--page-max)',
            margin: '0 auto',
            padding: 'var(--sp-6) var(--gutter) var(--sp-8)',
            minWidth: 0,
          }}
        >
          <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
            {entry !== null && entry.tabs.length > 1 && !SINGLE_SCREEN.has(entry.code) ? (
              <SectionTabs
                tabs={entry.tabs}
                active={section}
                onSelect={(tab) => navigate(entry.code, { tab })}
              />
            ) : null}
            {/* Разделы, ставшие вкладками, рисуются друг под другом в «Мониторинге»:
                  там по карточке на раздел, и переключать вкладки ради них незачем. */}
            {entry?.code === 'monitoring'
              ? entry.tabs.map((tab) => (
                  <Section
                    key={tab}
                    section={tab}
                    sections={sections}
                    canEdit={canEdit}
                    currentUserId={profile.user_id}
                    params={route.params}
                    onParams={setParams}
                  />
                ))
              : null}
            {entry?.code === 'monitoring' ? null : (
              <Section
                section={section}
                sections={sections}
                canEdit={canEdit}
                currentUserId={profile.user_id}
                params={route.params}
                onParams={setParams}
              />
            )}
          </div>
        </main>
      </div>
    </DepartmentsContext.Provider>
  );
}

/**
 * Меню шапки: рабочие пункты на виду, настройки — под «Настройки».
 *
 * Состав пунктов — `menu.ts`; какие из них видны — решают права с сервера.
 */
function TopNav({
  sections,
  active,
  onSelect,
}: {
  readonly sections: readonly string[];
  readonly active: string;
  readonly onSelect: (code: string) => void;
}): ReactElement {
  const messages = useMessages();
  const [open, setOpen] = useState(false);

  const primary = visibleEntries(PRIMARY_MENU, sections);
  const settings = visibleEntries(SETTINGS_MENU, sections);
  const settingsActive = settings.some((entry) => entry.code === active);

  const item = (selected: boolean): CSSProperties => ({
    border: 'none',
    borderRadius: 'var(--r-pill)',
    padding: '0 var(--sp-4)',
    height: 'var(--btn-h)',
    whiteSpace: 'nowrap',
    fontSize: 'var(--fs-lg)',
    fontWeight: selected ? 'var(--fw-semibold)' : 'var(--fw-medium)',
    background: selected ? 'var(--surface)' : 'transparent',
    color: selected ? 'var(--accent)' : 'var(--text-2)',
    boxShadow: selected ? 'var(--shadow-active)' : 'none',
  });

  const menu = useRef<HTMLElement>(null);
  const close = useCallback(() => setOpen(false), []);

  useDismiss(open, close, menu);

  // Переход в другой раздел — откуда угодно, хоть кнопкой «Назад», — закрывает меню.
  useEffect(close, [active, close]);

  return (
    <nav
      ref={menu}
      style={{
        margin: '0 auto',
        display: 'flex',
        gap: 'var(--sp-1)',
        padding: 'var(--sp-1)',
        background: 'var(--bg)',
        borderRadius: 'var(--r-pill)',
        position: 'relative',
      }}
    >
      {primary.map((entry) => (
        <button
          key={entry.code}
          type="button"
          aria-current={entry.code === active ? 'page' : undefined}
          onClick={() => {
            setOpen(false);
            onSelect(entry.code);
          }}
          style={item(entry.code === active)}
        >
          {navLabel(messages, entry.code)}
        </button>
      ))}

      {settings.length === 0 ? null : (
        <>
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            style={item(settingsActive)}
          >
            {`${settingsActive ? navLabel(messages, active) : messages.cabinet.common.settings} ▾`}
          </button>

          {open ? (
            <div
              role="menu"
              style={{
                position: 'absolute',
                top: 'calc(100% + var(--sp-2))',
                right: 0,
                minWidth: '220px',
                background: 'var(--surface)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-md)',
                boxShadow: 'var(--shadow-pop)',
                padding: 'var(--sp-2)',
                display: 'grid',
                gap: 'var(--sp-1)',
                zIndex: 30,
              }}
            >
              {settings.map((entry) => (
                <button
                  key={entry.code}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onSelect(entry.code);
                  }}
                  style={{
                    textAlign: 'left',
                    border: 'none',
                    borderRadius: 'var(--r-sm)',
                    padding: 'var(--sp-2) var(--sp-3)',
                    fontSize: 'var(--fs)',
                    fontWeight: entry.code === active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                    background: entry.code === active ? 'var(--accent-soft)' : 'transparent',
                    color: entry.code === active ? 'var(--accent-ink)' : 'var(--text-2)',
                  }}
                >
                  {navLabel(messages, entry.code)}
                </button>
              ))}
            </div>
          ) : null}
        </>
      )}
    </nav>
  );
}

/** Вкладки пункта, собранного из нескольких разделов: «Пользователи | Курьеры». */
function SectionTabs({
  tabs,
  active,
  onSelect,
}: {
  readonly tabs: readonly string[];
  readonly active: string;
  readonly onSelect: (tab: string) => void;
}): ReactElement {
  const messages = useMessages();

  return (
    <div role="tablist" style={{ display: 'flex', gap: 'var(--sp-2)' }}>
      {tabs.map((tab) => (
        <button
          key={tab}
          type="button"
          role="tab"
          aria-selected={tab === active}
          onClick={() => onSelect(tab)}
          style={{
            ...pillStyle,
            background: tab === active ? 'var(--accent)' : 'var(--surface)',
            borderColor: tab === active ? 'var(--accent)' : 'var(--line)',
            color: tab === active ? 'var(--text-on-accent)' : 'var(--text-2)',
          }}
        >
          {navLabel(messages, tab)}
        </button>
      ))}
    </div>
  );
}

function navLabel(messages: ReturnType<typeof useMessages>, code: string): string {
  return messages.cabinet.nav[code as keyof typeof messages.cabinet.nav] ?? code;
}

/**
 * Направление в шапке и переход в соседнее (Р-015).
 *
 * Переход открывает кабинет соседа в той же вкладке: данные инсталляций не смешиваются
 * (FR-413), у соседа действует своя сессия или его форма входа с подставленным номером.
 * Без соседей — просто код направления, как раньше.
 */
function InstallationSwitch({
  current,
  peers,
}: {
  readonly current: string;
  readonly peers: readonly string[];
}): ReactElement {
  const messages = useMessages();
  const [failed, setFailed] = useState(false);

  // Одна инсталляция — переключать нечего: её код в шапке ничего не говорит логисту,
  // место занимает логотип.
  if (peers.length === 0) {
    return <></>;
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
      <select
        aria-label={messages.cabinet.common.installation}
        className="num"
        value={current}
        onChange={(event) => {
          setFailed(false);
          void api
            .switchInstallation(event.target.value)
            .then(({ url }) => window.location.assign(url))
            .catch(() => setFailed(true));
        }}
        style={{ fontWeight: 'var(--fw-label)' }}
      >
        {[current, ...peers].map((code) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </select>

      {failed ? (
        <span style={{ color: 'var(--danger)' }}>{messages.cabinet.common.switchFailed}</span>
      ) : null}
    </div>
  );
}

/** Значок — тема, в которую переключит кнопка: в светлой луна, в тёмной солнце. */
function MoonIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SunIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const THEME_KEY = 'tms.theme';

function readStoredTheme(): 'light' | 'dark' {
  try {
    return window.localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/** Код короткой ссылки из адреса `/t/<code>`: маршрутизатор хранит только раздел. */
function publicSegment(): string {
  const parts = window.location.pathname.split('/').filter((part) => part !== '');

  return parts[1] ?? '';
}

function Section({
  section,
  sections,
  canEdit,
  currentUserId,
  params,
  onParams,
}: {
  readonly section: string;
  readonly sections: readonly string[];
  readonly canEdit: boolean;
  readonly currentUserId: string;
  readonly params: URLSearchParams;
  readonly onParams: (next: Record<string, string | undefined>) => void;
}): ReactElement {
  const messages = useMessages();

  switch (section) {
    case 'flow':
      return <FlowPage params={params} onParams={onParams} />;
    case 'live_map':
      return <LiveMapPage />;
    case 'resolutions':
      return <ResolutionsPage />;
    case 'returns':
      return <ReturnsPage />;
    case 'import':
      return <ImportPage />;
    case 'locations':
      return <DirectoriesPage section="locations" canEdit={canEdit} />;
    case 'zones':
      return <DirectoriesPage section="zones" canEdit={canEdit} />;
    case 'pickup_matrix':
      return <PickupMatrixPage canEdit={canEdit} />;
    case 'integrations':
      return <IntegrationsPage canEdit={canEdit} />;
    case 'parameters':
      return <ParametersPage canEdit={canEdit} />;
    case 'slo':
      return <SloPage />;
    case 'runs':
      return <RunsPage />;
    case 'audit':
      return <AuditPage canSeeRaw={canEdit} />;
    case 'templates':
      return <TemplatesPage canEdit={canEdit} />;
    case 'users':
    case 'drivers':
      return (
        <UsersPage
          canEditModes={sections.includes('users')}
          canManageUsers={canEdit && sections.includes('users')}
          currentUserId={currentUserId}
          // Профиль несёт разделы, а не отдельные права: `drivers:create` по матрице
          // (разд. 2.1) есть только у администратора — тот же признак, что у правки
          // остальных справочников.
          canCreateDrivers={canEdit}
          // `drivers:update` по матрице (разд. 2.1) — у администратора и логиста: правка
          // курьера доступна всем, кому виден раздел курьеров. Чужой отдел сервер
          // отклонит `department_forbidden`.
          canEditDrivers={sections.includes('drivers')}
          role={params.get('role')}
          onRole={(role) => onParams({ role: role ?? undefined })}
        />
      );
    default:
      // Прямой переход на раздел, недоступный роли: сервер ответит `403` и запишет
      // это в аудит (FR-201), а экран говорит об этом словами, а не пустотой.
      return <EmptyState text={messages.cabinet.common.forbidden} />;
  }
}
